// AI-invulhulp met spraakknop (blok D, opdracht 20-07).
//
// Voor mensen die moeite hebben met typen of lezen, taalproblemen
// hebben of slechtziend zijn: vertel (of typ) wat er aan de hand is en
// de AI vult het formulier vooraf in — altijd als voorstel dat de
// klant zelf controleert en bevestigt. Spraak loopt via de Web Speech
// API van de browser (nl-NL, geen externe dienst); zonder ondersteuning
// blijft het tekstvak gewoon werken.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Mic, MicOff, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  retourApi,
  vertaalRetourFout,
  type RetourInvulhulpAntwoord,
} from "@/lib/api";

type SpeechRecognitionachtig = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: unknown) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
};

function maakHerkenner(): SpeechRecognitionachtig | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionachtig;
    webkitSpeechRecognition?: new () => SpeechRecognitionachtig;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  if (!Ctor) return null;
  const herkenner = new Ctor();
  herkenner.lang = "nl-NL";
  herkenner.continuous = true;
  herkenner.interimResults = false;
  return herkenner;
}

export function RetourInvulhulp({
  token,
  onVoorstel,
}: {
  token: string;
  onVoorstel: (voorstel: RetourInvulhulpAntwoord) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tekst, setTekst] = useState("");
  const [luistert, setLuistert] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const herkennerRef = useRef<SpeechRecognitionachtig | null>(null);
  const [spraakBeschikbaar] = useState(
    () => maakHerkenner() !== null,
  );

  useEffect(() => {
    return () => {
      herkennerRef.current?.stop();
    };
  }, []);

  function wisselLuisteren() {
    if (luistert) {
      herkennerRef.current?.stop();
      setLuistert(false);
      return;
    }
    const herkenner = maakHerkenner();
    if (!herkenner) return;
    herkennerRef.current = herkenner;
    herkenner.onresult = (event) => {
      const e = event as {
        resultIndex: number;
        results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>;
      };
      let extra = "";
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const resultaat = e.results[i];
        if (resultaat.isFinal) extra += resultaat[0].transcript;
      }
      if (extra) {
        setTekst((huidig) => `${huidig} ${extra}`.trim());
      }
    };
    herkenner.onend = () => setLuistert(false);
    herkenner.onerror = () => setLuistert(false);
    herkenner.start();
    setLuistert(true);
  }

  async function vraagVoorstel() {
    if (tekst.trim().length < 3) return;
    setFout(null);
    setBezig(true);
    try {
      const voorstel = await retourApi.invulhulp(token, tekst.trim());
      onVoorstel(voorstel);
    } catch (err) {
      setFout(vertaalRetourFout(err, t("retour.invulhulp.fout")));
    } finally {
      setBezig(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-3 rounded-xl border border-dashed border-brand-cyan bg-[#e5f5fc]/50 p-4 text-left text-[13.5px] transition-colors hover:bg-[#e5f5fc]"
      >
        <Sparkles className="h-5 w-5 shrink-0 text-brand-cyan" aria-hidden />
        <span>
          <span className="block font-medium">
            {t("retour.invulhulp.titel")}
          </span>
          <span className="block text-muted-foreground">
            {t("retour.invulhulp.uitleg_kort")}
          </span>
        </span>
      </button>
    );
  }

  return (
    <section
      aria-label={t("retour.invulhulp.titel")}
      className="rounded-xl border bg-card p-4 shadow-sm space-y-3"
    >
      <p className="text-[13.5px] font-medium">
        {t("retour.invulhulp.titel")}
      </p>
      <p className="text-[12.5px] text-muted-foreground">
        {t("retour.invulhulp.uitleg")}
      </p>
      <div className="space-y-1.5">
        <Label htmlFor="invulhulp-tekst" className="sr-only">
          {t("retour.invulhulp.tekst_label")}
        </Label>
        <Textarea
          id="invulhulp-tekst"
          value={tekst}
          onChange={(e) => setTekst(e.target.value)}
          placeholder={t("retour.invulhulp.placeholder")}
          className="min-h-[100px]"
        />
      </div>
      <p aria-live="polite" className="sr-only">
        {luistert ? t("retour.invulhulp.luistert") : ""}
      </p>
      <div className="flex flex-wrap gap-2">
        {spraakBeschikbaar && (
          <Button
            type="button"
            variant={luistert ? "destructive" : "outline"}
            className="h-11"
            aria-pressed={luistert}
            onClick={wisselLuisteren}
          >
            {luistert ? (
              <MicOff className="mr-2 h-4 w-4" aria-hidden />
            ) : (
              <Mic className="mr-2 h-4 w-4" aria-hidden />
            )}
            {luistert
              ? t("retour.invulhulp.stop_opname")
              : t("retour.invulhulp.start_opname")}
          </Button>
        )}
        <Button
          type="button"
          className="h-11 flex-1"
          disabled={bezig || tekst.trim().length < 3}
          onClick={vraagVoorstel}
        >
          {bezig && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          )}
          {bezig
            ? t("retour.invulhulp.bezig")
            : t("retour.invulhulp.invullen")}
        </Button>
      </div>
      {fout && (
        <p role="alert" aria-live="polite" className="text-sm text-destructive">
          {fout}
        </p>
      )}
      <p className="text-[12px] text-muted-foreground">
        {t("retour.invulhulp.disclaimer")}
      </p>
    </section>
  );
}
